/** RELANCES — le chiffre d'affaires invisible : les clients qui ont dépassé leur propre rythme. */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, Wand2 } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import {
  Btn, Choix, Erreur, Feuille, Micro, Squelette, Vide, Zone,
} from "../components/Bits";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { euros } from "../lib/format";

export default function Relances() {
  const { compte, version } = useApp();
  const [data, setData] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [onglet, setOnglet] = useState("retards");
  const [cible, setCible] = useState(null);
  const [canal, setCanal] = useState("email");
  const [ton, setTon] = useState("chaleureux");
  const [message, setMessage] = useState("");
  const [redaction, setRedaction] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/relances");
      setData(data);
    } catch (e) {
      setErreur(messageErreur(e, "Les relances ne sont pas disponibles."));
    } finally {
      setCharge(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger, version]);

  const ouvrir = (client) => {
    setCible(client);
    setCanal("email");
    setMessage("");
  };

  const rediger = async () => {
    setRedaction(true);
    try {
      const creneaux = (data?.creux || []).slice(0, 2).map((c) => `${c.jour_label} ${c.label.split(" →")[0]}`);
      const { data: res } = await api.post("/relances/redaction", {
        client_id: cible.client_id,
        canal,
        ton,
        creneaux,
      });
      setMessage(res.texte);
      if (res.source === "modele_local")
        toast.info("L'assistant est indisponible : voici un modèle de base, à personnaliser.");
    } catch (e) {
      toast.error(messageErreur(e, "Rédaction impossible."));
    } finally {
      setRedaction(false);
    }
  };

  const envoyer = async () => {
    if (message.trim().length < 5) return toast.error("Écrivez un message avant d'envoyer.");
    setEnvoi(true);
    try {
      await api.post("/relances/envoi", {
        client_id: cible.client_id,
        canal,
        message,
        objet: canal === "email" ? `Un mot de ${compte?.activite}` : undefined,
      });
      toast.success(`Message envoyé à ${cible.nom}.`);
      setCible(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e, "Envoi impossible."));
    } finally {
      setEnvoi(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message copié. Collez-le dans WhatsApp ou Instagram.");
    } catch {
      toast.error("Copie impossible sur ce navigateur.");
    }
  };

  if (!compte?.plan?.relances)
    return (
      <div className="pt-4">
        <Vide
          icone={Sparkles}
          titre="Les relances font partie du plan Pro"
          texte="Servae repère les clients qui ont dépassé leur rythme habituel et rédige le message à leur envoyer. C'est la fonction qui rembourse ton abonnement."
        />
      </div>
    );

  return (
    <div className="pb-8">
      <div className="flex items-end justify-between gap-4 pb-4 border-b border-[var(--rule)]">
        <div>
          <Micro>Relances</Micro>
          <h1 className="display text-[26px] md:text-[32px] mt-1.5">Le CA qui dort</h1>
        </div>
        {data && (
          <div className="text-right shrink-0">
            <div className="num text-[24px] leading-none" data-testid="ca-dormant">
              {euros(data.ca_dormant)}
            </div>
            <Micro muted className="mt-1">à récupérer</Micro>
          </div>
        )}
      </div>

      {charge ? (
        <div className="pt-5">
          <Squelette lignes={4} />
        </div>
      ) : erreur ? (
        <div className="pt-5">
          <Erreur message={erreur} surReessai={charger} />
        </div>
      ) : (
        <>
          <Tabs value={onglet} onValueChange={setOnglet} className="pt-4">
            <TabsList className="bg-transparent p-0 h-auto gap-1 border-b border-[var(--rule)] w-full justify-start rounded-none">
              <TabsTrigger
                value="retards"
                data-testid="onglet-retards"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--accent)] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em]"
              >
                En retard ({data.retards.length})
              </TabsTrigger>
              <TabsTrigger
                value="creux"
                data-testid="onglet-creux"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[var(--accent)] data-[state=active]:bg-transparent data-[state=active]:shadow-none px-3 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em]"
              >
                Creux ({data.creux.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {onglet === "retards" ? (
            data.retards.length === 0 ? (
              <Vide
                icone={Sparkles}
                titre="Personne à relancer"
                texte="Tous tes clientes réguliers sont à jour, ou ont déjà un rendez-vous. Servae te prévient dès que l'un d'eux dépasse son rythme."
              />
            ) : (
              <div className="pt-2">
                <p className="text-[13px] text-[var(--ink-soft)] py-3">
                  Ces personnes venaient régulièrement et ont dépassé leur propre rythme. Classées
                  par panier moyen : commencez par le haut.
                </p>
                {data.retards.map((r) => (
                  <div
                    key={r.client_id}
                    className="flex items-center gap-3 py-3.5 border-b border-[var(--rule)]"
                    data-testid={`retard-${r.client_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold truncate">{r.nom}</div>
                      <div className="text-[13px] text-[var(--ink-soft)]">
                        Vient tous les {r.rythme_jours} jours · absent depuis {r.jours_depuis}
                        {" "}({r.retard_jours} j de retard)
                        {r.derniere_prestation ? ` · ${r.derniere_prestation}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0 hidden sm:block">
                      <div className="num text-[15px]">{euros(r.panier_moyen)}</div>
                      <Micro muted>panier</Micro>
                    </div>
                    <Btn
                      taille="sm"
                      variante="secondaire"
                      onClick={() => ouvrir(r)}
                      data-testid={`relancer-${r.client_id}`}
                    >
                      Relancer
                    </Btn>
                  </div>
                ))}
              </div>
            )
          ) : data.creux.length === 0 ? (
            <Vide
              titre="Semaine complète"
              texte="Aucun trou exploitable dans les sept prochains jours. Beau travail."
            />
          ) : (
            <div className="pt-2">
              <p className="text-[13px] text-[var(--ink-soft)] py-3">
                Les places libres des sept prochains jours. Propose-les dans tes relances.
              </p>
              {data.creux.map((c, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 py-3 border-b border-[var(--rule)]"
                  data-testid={`creux-${i}`}
                >
                  <div>
                    <div className="text-[14px] font-semibold first-letter:uppercase">{c.jour_label}</div>
                    <div className="num text-[13px] text-[var(--ink-soft)]">{c.label}</div>
                  </div>
                  <Micro muted>{Math.round(c.minutes / 60)} h libres</Micro>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Feuille
        ouvert={!!cible}
        surFermeture={() => setCible(null)}
        micro="Relance"
        titre={cible ? cible.nom : ""}
        testid="feuille-relance"
        pied={
          <>
            <Btn variante="nu" onClick={copier} data-testid="btn-copier-message">
              Copier
            </Btn>
            <Btn icone={Send} onClick={envoyer} charge={envoi} data-testid="btn-envoyer-relance">
              Envoyer par e-mail
            </Btn>
          </>
        }
      >
        {cible && (
          <div className="space-y-4">
            <div className="surface rounded-[4px] p-4">
              <Micro muted>Contexte</Micro>
              <p className="text-[14px] mt-2">
                Vient habituellement tous les {cible.rythme_jours} jours. Dernière visite il y a{" "}
                {cible.jours_depuis} jours. Panier moyen {euros(cible.panier_moyen)}.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Micro muted>Canal</Micro>
                <p className="text-[14px] mt-2" data-testid="canal-relance-email">
                  E-mail{cible.email ? ` · ${cible.email}` : " (adresse manquante)"}
                </p>
              </div>
              <Choix
                label="Ton"
                valeur={ton}
                surChangement={setTon}
                testid="choix-ton"
                options={[
                  { valeur: "chaleureux", label: "Chaleureux, tutoiement" },
                  { valeur: "pro", label: "Professionnel, vouvoiement" },
                  { valeur: "direct", label: "Court et direct" },
                ]}
              />
            </div>

            <Btn
              variante="fantome"
              icone={Wand2}
              onClick={rediger}
              charge={redaction}
              className="w-full"
              data-testid="btn-rediger-ia"
            >
              {message ? "Proposer une autre formulation" : "Rédiger le message pour moi"}
            </Btn>

            <Zone
              label="Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Écris ton message, ou laisse l'assistant le rédiger."
              indice="Relisez toujours avant d'envoyer"
              data-testid="champ-message-relance"
            />

            {!data?.canaux?.email?.actif && (
              <p className="text-[13px] text-[var(--ambre)]">
                Le canal e-mail n'est pas encore branché. Tu peux
                copier le message et l'envoyer toi-même, ou brancher tes identifiants dans
                Réglages.
              </p>
            )}
          </div>
        )}
      </Feuille>
    </div>
  );
}
